import React from 'react';

import {Button, Card, message} from 'antd';
import {resetPassword, signOut} from 'aws-amplify/auth';
import type {FormikProps} from 'formik';
import {withFormik} from 'formik';
import {Form} from 'formik-antd';
import moment from 'moment';

import {AnchorLink} from '../AnchorLink';

import {Extra} from './Shared';

import {getErrorInfo} from 'lib/errorUtils';
import type {IAuthProps} from 'types';
import {AuthState} from 'types';

interface IFormValues {
  email: string;
}

// eslint-disable-next-line react-refresh/only-export-components
const PasswordExpiryWarningComponent: React.FC<FormikProps<IFormValues> & IAuthProps> = ({
  isSubmitting,
  setState,
  passwordExpiresAt,
  values,
  currentUser,
  email: userEmail
}) => {
  const skipWarning = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setState({authState: AuthState.signedIn, currentUser, email: userEmail});
  };

  const backToSignIn = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setState({authState: AuthState.signIn, email: values.email});
  };

  const formatExpiryDate = (dateStr: string) => {
    try {
      return moment(dateStr).fromNow();
    } catch {
      return 'soon';
    }
  };

  return (
    <Card
      title="Your password will expire soon"
      bodyStyle={{paddingTop: 24}}
      extra={<AnchorLink onClick={backToSignIn}>Back to Sign In</AnchorLink>}>
      <p style={{marginBottom: 24}}>
        Your password expires <strong>{formatExpiryDate(passwordExpiresAt || '')}</strong>. Please
        reset your password to continue using your account without interruption.
      </p>
      <Form layout="vertical">
        <Button block size="large" type="primary" loading={isSubmitting} htmlType="submit">
          Send reset code
        </Button>
        <Extra>
          <AnchorLink onClick={skipWarning}>Skip for now</AnchorLink>
        </Extra>
      </Form>
    </Card>
  );
};

export const PasswordExpiryWarning = withFormik<IAuthProps, IFormValues>({
  mapPropsToValues: ({email = ''}) => ({email}),
  handleSubmit: async ({email}, {props, setSubmitting}) => {
    try {
      // Sign out current user before initiating password reset
      await signOut();
      await resetPassword({username: email});
      props.setState({authState: AuthState.forgotPasswordSubmit, email});
    } catch (error: unknown) {
      // Safely extract error information
      const {message: errorMessage} = getErrorInfo(error);
      void message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }
})(PasswordExpiryWarningComponent);
